import React, { createContext, useEffect, useState, useContext } from 'react';
import {
  apiGetMe,
  clearStoredToken,
  getStoredToken,
  setStoredToken,
} from '../services/api';


export const AuthContext = createContext({});

const USER_KEY = 'auth_user';

// As funções getStoredUser, setStoredUser e clearStoredUser são usadas para gerenciar os dados do usuário autenticado no armazenamento local do navegador (localStorage). Elas permitem obter, armazenar e remover os dados do usuário conforme necessário para manter a sessão de autenticação ativa na aplicação.

function getStoredUser() {
  try {
    const rawUser = localStorage.getItem(USER_KEY);
    return rawUser ? JSON.parse(rawUser) : null;
  } catch (_error) {
    return null;
  }
}

function setStoredUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearStoredUser() {
  localStorage.removeItem(USER_KEY);
}

// O AuthProvider é um componente que envolve a aplicação e fornece o contexto de autenticação para todos os componentes filhos. Ele gerencia o estado do usuário autenticado, o token JWT e o estado de carregamento da sessão. O useEffect é usado para restaurar a sessão do usuário ao carregar a aplicação, verificando se há um token armazenado e buscando os dados do usuário correspondente usando a função apiGetMe. As funções login e logout são usadas para atualizar o estado de autenticação e gerenciar o armazenamento do token e dos dados do usuário no localStorage.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [token, setToken] = useState(() => getStoredToken());
  const [loadingSession, setLoadingSession] = useState(true);

  useEffect(() => {
    async function restoreSession() {
      if (!token) {
        setLoadingSession(false);
        return;
      }

      const data = await apiGetMe(token);
      if (data?.user) {
        setUser(data.user);
        setStoredUser(data.user);
      } else {
        clearStoredToken();
        clearStoredUser();
        setToken(null);
        setUser(null);
      }

      setLoadingSession(false);
    }

    restoreSession();
  }, [token]);

  const login = (userData, jwtToken) => {
    setUser(userData);
    setToken(jwtToken);
    setStoredToken(jwtToken);
    setStoredUser(userData);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    clearStoredToken();
    clearStoredUser();
  };

  return (
    <AuthContext.Provider value={{ user, token, signed: !!user, loadingSession, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// A função do AuthContext é fornecer um contexto de autenticação para a aplicação React, permitindo que os componentes acessem o estado de autenticação, os dados do usuário e as funções de login/logout de forma fácil e centralizada.
// O AuthContext é criado usando createContext, e o AuthProvider é um componente que envolve a aplicação, fornecendo o estado de autenticação e as funções de login/logout para todos os componentes filhos. O useAuth é um hook personalizado que facilita o acesso ao contexto de autenticação em qualquer componente da aplicação.
// Quando falo sobre componentes filhos, estou me referindo a qualquer componente que esteja dentro do AuthProvider na hierarquia de componentes do React. Esses componentes podem acessar o contexto de autenticação usando o hook useAuth para verificar se o usuário está autenticado, obter os dados do usuário ou chamar as funções de login/logout conforme necessário.